import type { Shape } from '@pqina/pintura';

export type CanvasPresetId =
  | 'square'
  | 'portrait'
  | 'story'
  | 'landscape'
  | 'hd';

export type CanvasDefinition = {
  preset: CanvasPresetId | 'custom';
  width: number;
  height: number;
  backgroundColor: string;
};

export type TemplateLayerKind = 'text' | 'shape' | 'image' | 'background';
export type TemplateShapeType =
  | 'rectangle'
  | 'ellipse'
  | 'triangle'
  | 'straightLine'
  | 'wavyLine'
  | 'arrow'
  | 'diamond'
  | 'star';

/**
 * A template holds at most one `background` layer. It is pinned to the bottom
 * of the stack and paints its `fillColor` behind an optional image. Its frame
 * can be moved, resized, and rotated like other layers.
 */
export type TemplateLayer = {
  id: string;
  name: string;
  kind: TemplateLayerKind;
  locked: boolean;
  visible: boolean;
  /** Clockwise degrees. The mapper converts this to Pintura radians. */
  rotation?: number;
  position: { x: number; y: number };
  size: { width: number; height: number };
  text?: string;
  typography?: {
    fontFamily: string;
    fontSize: number;
    fontWeight: 'normal' | 'bold';
    fontStyle: 'normal' | 'italic';
    textAlign: 'left' | 'center' | 'right';
    color: string;
    lineHeight: number;
    letterSpacing: number;
  };
  fillColor?: string;
  cornerRadius?: number;
  shapeType?: TemplateShapeType;
  image?: {
    src?: string;
    fit: 'cover' | 'contain' | 'stretch' | 'natural';
    opacity?: number;
    borderColor?: string;
    borderWidth?: number;
  };
};

export type TemplateDefinition = {
  version: 1;
  id: string;
  name: string;
  canvas: CanvasDefinition;
  layers: TemplateLayer[];
};

export const TEMPLATE_EDITOR_THUMBNAIL_WIDTH = 600;

export const canvasPresets: Array<{
  id: CanvasPresetId;
  label: string;
  width: number;
  height: number;
}> = [
  { id: 'square', label: 'Square', width: 1080, height: 1080 },
  { id: 'portrait', label: 'Portrait', width: 1080, height: 1350 },
  { id: 'story', label: 'Story / Reel', width: 1080, height: 1920 },
  { id: 'landscape', label: 'Landscape', width: 1200, height: 628 },
  { id: 'hd', label: 'HD', width: 1920, height: 1080 }
];

/**
 * The editor opens immediately; 4:5 portrait is the product default for a
 * newly created template. Canvas size can subsequently be changed in Layers.
 */
export const createDefaultTemplate = (
  id: string,
  name = 'Overlay 1'
): TemplateDefinition => ({
  version: 1,
  id,
  name,
  canvas: {
    preset: 'portrait',
    width: 1080,
    height: 1350,
    backgroundColor: 'transparent'
  },
  layers: []
});

/**
 * Creates a valid empty definition for legacy records that predate the
 * versioned editor JSON. Keep the saved aspect-ratio metadata and the canvas
 * in sync so a cloned legacy overlay opens with the expected dimensions.
 */
export const createDefaultTemplateForAspectRatio = (
  id: string,
  name: string,
  aspectRatio: string
): TemplateDefinition => {
  const template = createDefaultTemplate(id, name);
  const preset =
    aspectRatio === '1:1'
      ? canvasPresets.find(({ id: presetId }) => presetId === 'square')
      : aspectRatio === '9:16'
        ? canvasPresets.find(({ id: presetId }) => presetId === 'story')
        : aspectRatio === '16:9'
          ? canvasPresets.find(({ id: presetId }) => presetId === 'hd')
          : aspectRatio === '300:157'
            ? canvasPresets.find(
                ({ id: presetId }) => presetId === 'landscape'
              )
            : canvasPresets.find(
                ({ id: presetId }) => presetId === 'portrait'
              );

  if (!preset) return template;

  return {
    ...template,
    canvas: {
      preset: preset.id,
      width: preset.width,
      height: preset.height,
      backgroundColor: 'transparent'
    }
  };
};

export type TemplatePinturaShape = Shape & Record<string, unknown>;

const hexToColor = (value: string) =>
  [1, 3, 5].map((index) => parseInt(value.slice(index, index + 2), 16) / 255);

const colorToHex = (value: unknown, fallback: string) =>
  Array.isArray(value) && value.length >= 3
    ? `#${value
        .slice(0, 3)
        .map((channel) =>
          Math.round(Number(channel) * 255)
            .toString(16)
            .padStart(2, '0')
        )
        .join('')}`
    : fallback;

const triangleImage = (fillColor: string) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><path fill="${fillColor}" d="M50 0 100 100H0z"/></svg>`
  )}`;

const wavyLineImage = (strokeColor: string) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 24"><path fill="none" stroke="${strokeColor}" stroke-linecap="round" stroke-width="6" d="M3 12c12-16 22 16 34 0s22 16 34 0 17 0 26 0"/></svg>`
  )}`;

const arrowImage = (fillColor: string) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 80"><path fill="${fillColor}" d="M0 32h96V8l64 32-64 32V48H0z"/></svg>`
  )}`;

const diamondImage = (fillColor: string) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><path fill="${fillColor}" d="M50 0l50 50-50 50L0 50z"/></svg>`
  )}`;

const starImage = (fillColor: string) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><path fill="${fillColor}" d="m50 2 11.2 34.5H97L68 57l11.2 34.5L50 70 20.8 91.5 32 57 3 36.5h35.8z"/></svg>`
  )}`;

/**
 * This editor uses only Pintura annotations and always passes canvas pixels.
 * Crop and decoration coordinate systems are intentionally out of scope and
 * can therefore never leak into the template definition.
 */
export const toPinturaShape = (layer: TemplateLayer): TemplatePinturaShape => {
  const base = {
    id: layer.id,
    opacity:
      layer.visible && layer.kind === 'image'
        ? layer.image?.opacity ?? 1
        : layer.visible
        ? 1
        : 0,
    rotation: ((layer.rotation ?? 0) * Math.PI) / 180,
    // The application sidebar owns all edits. The canvas is deliberately only a
    // selection and positioning surface for unlocked layers.
    disableStyle: true,
    disableInput: true,
    disableNewline: false,
    disableTextLayout: true,
    disableRemove: true,
    disableDuplicate: true,
    disableReorder: true,
    disableResize: layer.locked,
    disableManipulate: layer.locked,
    disableMove: layer.locked,
    disableSelect: !layer.visible
  };
  const rectangle = {
    x: layer.position.x,
    y: layer.position.y,
    width: layer.size.width,
    height: layer.size.height
  };

  if (layer.kind === 'background') {
    const isEmpty =
      (!layer.fillColor || layer.fillColor === 'transparent') &&
      !layer.image?.src;
    return {
      ...base,
      ...rectangle,
      backgroundColor:
        layer.fillColor && layer.fillColor !== 'transparent'
          ? hexToColor(layer.fillColor)
          : [0, 0, 0, 0],
      backgroundImage: layer.image?.src,
      // Pintura centres the image in its interactive frame while preserving
      // the image aspect ratio.
      backgroundSize: layer.image?.src ? 'contain' : undefined,
      disableSelect: !layer.visible || isEmpty,
      disableMove: layer.locked || isEmpty,
      disableResize: layer.locked || isEmpty,
      disableManipulate: layer.locked || isEmpty
    };
  }

  if (layer.kind === 'shape' && layer.shapeType === 'ellipse') {
    return {
      ...base,
      x: layer.position.x + layer.size.width / 2,
      y: layer.position.y + layer.size.height / 2,
      rx: layer.size.width / 2,
      ry: layer.size.height / 2,
      backgroundColor: hexToColor(layer.fillColor ?? '#3359c7')
    };
  }

  if (
    layer.kind === 'shape' &&
    (layer.shapeType === 'triangle' ||
      layer.shapeType === 'wavyLine' ||
      layer.shapeType === 'arrow' ||
      layer.shapeType === 'diamond' ||
      layer.shapeType === 'star')
  ) {
    return {
      ...base,
      ...rectangle,
      backgroundColor: [0, 0, 0, 0],
      backgroundImage: (() => {
        const fillColor = layer.fillColor ?? '#3359c7';
        if (layer.shapeType === 'triangle') return triangleImage(fillColor);
        if (layer.shapeType === 'wavyLine') return wavyLineImage(fillColor);
        if (layer.shapeType === 'arrow') return arrowImage(fillColor);
        if (layer.shapeType === 'diamond') return diamondImage(fillColor);
        return starImage(fillColor);
      })(),
      backgroundSize: { width: layer.size.width, height: layer.size.height }
    };
  }

  return {
    ...base,
    ...rectangle,
    ...(layer.kind === 'text'
      ? {
          text: layer.text,
          ...layer.typography,
          lineHeight: `${(layer.typography?.lineHeight ?? 1.1) * 100}%`,
          color: hexToColor(layer.typography?.color ?? '#000000')
        }
      : {}),
    ...(layer.kind === 'shape'
      ? {
          backgroundColor: hexToColor(layer.fillColor ?? '#3359c7'),
          cornerRadius: layer.cornerRadius ?? 0
        }
      : {}),
    ...(layer.kind === 'image'
      ? {
          backgroundImage: layer.image?.src,
          backgroundColor: layer.image?.src ? undefined : [0, 0, 0, 0],
          backgroundSize:
            layer.image?.fit === 'stretch'
              ? { width: layer.size.width, height: layer.size.height }
              : layer.image?.fit === 'natural'
              ? undefined
              : layer.image?.fit ?? 'cover',
          strokeColor:
            layer.image?.borderWidth && layer.image.borderWidth > 0
              ? hexToColor(layer.image.borderColor ?? '#000000')
              : undefined,
          strokeWidth: layer.image?.borderWidth ?? 0,
          cornerRadius: layer.cornerRadius ?? 0
        }
      : {}),
    ...base
  };
};

const percentageToCanvasValue = (
  value: unknown,
  total: number,
  fallback: number
) => {
  if (typeof value === 'string' && value.endsWith('%')) {
    const percentage = Number.parseFloat(value);
    return Number.isFinite(percentage) ? (percentage / 100) * total : fallback;
  }
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
};

const pinturaLineHeightToMultiplier = (
  value: unknown,
  fontSize: number,
  fallback: number
) => {
  if (typeof value === 'string' && value.endsWith('%')) {
    const percentage = Number.parseFloat(value);
    return Number.isFinite(percentage) ? percentage / 100 : fallback;
  }
  return typeof value === 'number' && fontSize > 0
    ? value / fontSize
    : fallback;
};

export const pinturaShapeToLayerPatch = (
  shape: Shape,
  canvas: CanvasDefinition,
  previousLayer: TemplateLayer
): Partial<TemplateLayer> => {
  const source = shape as TemplatePinturaShape;
  const ellipse =
    typeof source.rx === 'number' && typeof source.ry === 'number';
  const points = Array.isArray(source.points) ? source.points : undefined;
  const pathBounds = points?.reduce(
    (bounds, point) => ({
      left: Math.min(bounds.left, Number(point.x)),
      top: Math.min(bounds.top, Number(point.y)),
      right: Math.max(bounds.right, Number(point.x)),
      bottom: Math.max(bounds.bottom, Number(point.y))
    }),
    { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity }
  );
  const x = ellipse
    ? Number(source.x) - Number(source.rx)
    : pathBounds && Number.isFinite(pathBounds.left)
    ? pathBounds.left
    : source.x;
  const y = ellipse
    ? Number(source.y) - Number(source.ry)
    : pathBounds && Number.isFinite(pathBounds.top)
    ? pathBounds.top
    : source.y;
  const width = ellipse
    ? Number(source.rx) * 2
    : pathBounds && Number.isFinite(pathBounds.right)
    ? pathBounds.right - pathBounds.left
    : source.width;
  const height = ellipse
    ? Number(source.ry) * 2
    : pathBounds && Number.isFinite(pathBounds.bottom)
    ? pathBounds.bottom - pathBounds.top
    : source.height;
  const patch: Partial<TemplateLayer> = {
    rotation:
      typeof source.rotation === 'number' && Number.isFinite(source.rotation)
        ? (source.rotation * 180) / Math.PI
        : previousLayer.rotation ?? 0,
    position: {
      x: percentageToCanvasValue(x, canvas.width, previousLayer.position.x),
      y: percentageToCanvasValue(y, canvas.height, previousLayer.position.y)
    },
    size: {
      width: percentageToCanvasValue(
        width,
        canvas.width,
        previousLayer.size.width
      ),
      height: percentageToCanvasValue(
        height,
        canvas.height,
        previousLayer.size.height
      )
    }
  };
  if (typeof source.text === 'string') {
    patch.text = source.text;
    patch.typography = {
      fontFamily: String(source.fontFamily ?? 'Arial'),
      fontSize: Number(source.fontSize ?? 48),
      fontWeight: source.fontWeight === 'bold' ? 'bold' : 'normal',
      fontStyle: source.fontStyle === 'italic' ? 'italic' : 'normal',
      textAlign:
        source.textAlign === 'center' || source.textAlign === 'right'
          ? source.textAlign
          : 'left',
      color: colorToHex(source.color, '#000000'),
      lineHeight: pinturaLineHeightToMultiplier(
        source.lineHeight,
        Number(source.fontSize ?? 48),
        1.1
      ),
      letterSpacing: Number(source.letterSpacing ?? 0)
    };
  }
  return patch;
};

export const resizeTemplateCanvas = (
  template: TemplateDefinition,
  width: number,
  height: number,
  preset: CanvasDefinition['preset']
): TemplateDefinition => {
  const scaleX = width / template.canvas.width;
  const scaleY = height / template.canvas.height;
  return {
    ...template,
    canvas: { ...template.canvas, preset, width, height },
    layers: template.layers.map((layer) => ({
      ...layer,
      position: {
        x: layer.position.x * scaleX,
        y: layer.position.y * scaleY
      },
      size: {
        width: layer.size.width * scaleX,
        height: layer.size.height * scaleY
      }
    }))
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isHexColor = (value: unknown): value is string =>
  typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);

const isCanvasBackground = (value: unknown): value is string =>
  value === 'transparent' || isHexColor(value);

const defaultTypography = (): NonNullable<TemplateLayer['typography']> => ({
  fontFamily: 'Arial',
  fontSize: 48,
  fontWeight: 'normal',
  fontStyle: 'normal',
  textAlign: 'left',
  color: '#000000',
  lineHeight: 1.1,
  letterSpacing: 0
});

const parseGeometry = (value: Record<string, unknown>, index: number) => {
  if (
    !isRecord(value.position) ||
    !isRecord(value.size) ||
    !isFiniteNumber(value.position.x) ||
    !isFiniteNumber(value.position.y) ||
    !isFiniteNumber(value.size.width) ||
    !isFiniteNumber(value.size.height) ||
    value.size.width <= 0 ||
    value.size.height <= 0
  ) {
    throw new Error(`Layer ${index + 1} has invalid geometry.`);
  }
  return {
    position: { x: value.position.x, y: value.position.y },
    size: { width: value.size.width, height: value.size.height }
  };
};

const parseLayer = (
  value: unknown,
  index: number,
  canvas: CanvasDefinition
): TemplateLayer => {
  if (!isRecord(value))
    throw new Error(`Layer ${index + 1} must be an object.`);
  const kind = value.kind;
  if (
    kind !== 'text' &&
    kind !== 'shape' &&
    kind !== 'image' &&
    kind !== 'background'
  ) {
    throw new Error(`Layer ${index + 1} has an unsupported kind.`);
  }
  if (typeof value.id !== 'string') {
    throw new Error(`Layer ${index + 1} has an invalid id.`);
  }
  const layer: TemplateLayer = {
    id: value.id,
    name:
      typeof value.name === 'string'
        ? value.name
        : kind === 'text'
        ? 'Text layer'
        : kind === 'image'
        ? 'Image layer'
        : kind === 'background'
        ? 'Background'
        : 'Shape layer',
    kind,
    locked: value.locked === true,
    visible: value.visible !== false,
    rotation: isFiniteNumber(value.rotation) ? value.rotation : 0,
    // Older background definitions did not need to persist geometry. Keep
    // those files compatible by starting them at the canvas bounds.
    ...(kind === 'background'
      ? isRecord(value.position) && isRecord(value.size)
        ? parseGeometry(value, index)
        : {
            position: { x: 0, y: 0 },
            size: { width: canvas.width, height: canvas.height }
          }
      : parseGeometry(value, index))
  };
  if (kind === 'text') {
    const typography = isRecord(value.typography) ? value.typography : {};
    layer.text = typeof value.text === 'string' ? value.text : '';
    layer.typography = {
      ...defaultTypography(),
      fontFamily:
        typeof typography.fontFamily === 'string'
          ? typography.fontFamily
          : 'Arial',
      fontSize: isFiniteNumber(typography.fontSize) ? typography.fontSize : 48,
      fontWeight: typography.fontWeight === 'bold' ? 'bold' : 'normal',
      fontStyle: typography.fontStyle === 'italic' ? 'italic' : 'normal',
      textAlign:
        typography.textAlign === 'center' || typography.textAlign === 'right'
          ? typography.textAlign
          : 'left',
      color: isHexColor(typography.color) ? typography.color : '#000000',
      lineHeight: isFiniteNumber(typography.lineHeight)
        ? typography.lineHeight
        : 1.1,
      letterSpacing: isFiniteNumber(typography.letterSpacing)
        ? typography.letterSpacing
        : 0
    };
  }
  if (kind === 'shape') {
    layer.fillColor = isHexColor(value.fillColor) ? value.fillColor : '#3359c7';
    layer.cornerRadius = isFiniteNumber(value.cornerRadius)
      ? Math.max(0, value.cornerRadius)
      : 0;
    layer.shapeType =
      value.shapeType === 'ellipse' ||
      value.shapeType === 'triangle' ||
      value.shapeType === 'straightLine' ||
      value.shapeType === 'wavyLine' ||
      value.shapeType === 'arrow' ||
      value.shapeType === 'diamond' ||
      value.shapeType === 'star'
        ? value.shapeType
        : 'rectangle';
  }
  if (kind === 'image') {
    const image = isRecord(value.image) ? value.image : {};
    layer.image = {
      src: typeof image.src === 'string' ? image.src : undefined,
      // "natural" was a short-lived editor option; normalize old templates to
      // the closest supported, visible fit mode.
      fit:
        image.fit === 'cover' || image.fit === 'stretch'
          ? image.fit
          : 'contain',
      opacity: isFiniteNumber(image.opacity)
        ? Math.min(1, Math.max(0, image.opacity))
        : 1,
      borderColor: isHexColor(image.borderColor)
        ? image.borderColor
        : '#000000',
      borderWidth: isFiniteNumber(image.borderWidth)
        ? Math.max(0, image.borderWidth)
        : 0
    };
    layer.cornerRadius = isFiniteNumber(value.cornerRadius)
      ? Math.max(0, value.cornerRadius)
      : 0;
  }
  if (kind === 'background') {
    const image = isRecord(value.image) ? value.image : {};
    layer.fillColor = isCanvasBackground(value.fillColor)
      ? value.fillColor
      : '#ffffff';
    layer.image = {
      src: typeof image.src === 'string' ? image.src : undefined,
      // A background image is never cropped or distorted.
      fit: 'contain'
    };
  }
  return layer;
};

export const hasBackgroundLayer = (layers: TemplateLayer[]) =>
  layers.some((layer) => layer.kind === 'background');

/**
 * Converges any saved or imported template on the background rules: there is
 * at most one background and it sits at the bottom of the stack.
 */
const withBackgroundInvariants = (layers: TemplateLayer[]): TemplateLayer[] => {
  const backgrounds = layers.filter((layer) => layer.kind === 'background');
  if (backgrounds.length > 1) {
    throw new Error('An overlay can only contain one background layer.');
  }
  const [background] = backgrounds;
  if (!background) return layers;
  return [background, ...layers.filter((layer) => layer.kind !== 'background')];
};

/**
 * Produces the canonical, versioned model used for persistence. It accepts
 * versionless early POC templates and fills their documented defaults.
 */
export const normalizeTemplate = (value: unknown): TemplateDefinition => {
  if (
    !isRecord(value) ||
    !isRecord(value.canvas) ||
    !Array.isArray(value.layers)
  ) {
    throw new Error('This is not a valid overlay definition.');
  }
  if (value.version !== undefined && value.version !== 1) {
    throw new Error('This overlay version is not supported.');
  }
  if (
    typeof value.id !== 'string' ||
    typeof value.name !== 'string' ||
    !isFiniteNumber(value.canvas.width) ||
    !isFiniteNumber(value.canvas.height) ||
    value.canvas.width <= 0 ||
    value.canvas.height <= 0
  ) {
    throw new Error('This overlay has invalid canvas metadata.');
  }
  const preset = value.canvas.preset;
  const validPreset =
    preset === 'square' ||
    preset === 'portrait' ||
    preset === 'story' ||
    preset === 'landscape' ||
    preset === 'hd' ||
    preset === 'custom';
  if (!validPreset)
    throw new Error('This overlay has an unsupported canvas preset.');

  const canvas: CanvasDefinition = {
    preset,
    width: value.canvas.width,
    height: value.canvas.height,
    backgroundColor: isCanvasBackground(value.canvas.backgroundColor)
      ? value.canvas.backgroundColor
      : 'transparent'
  };
  const layers = withBackgroundInvariants(
    value.layers.map((layer, index) => parseLayer(layer, index, canvas))
  );
  if (new Set(layers.map((layer) => layer.id)).size !== layers.length) {
    throw new Error('Overlay layer IDs must be unique.');
  }
  return { version: 1, id: value.id, name: value.name, canvas, layers };
};

export const serializeTemplate = (template: TemplateDefinition) =>
  JSON.stringify(normalizeTemplate(template), null, 2);

export const parseTemplate = (json: string): TemplateDefinition =>
  normalizeTemplate(JSON.parse(json) as unknown);

export const createLayer = (
  kind: TemplateLayerKind,
  canvas: CanvasDefinition,
  shapeType: TemplateShapeType = 'rectangle'
): TemplateLayer => {
  const id = `layer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const base = { id, locked: false, visible: true, rotation: 0 };
  if (kind === 'background') {
    return {
      ...base,
      kind,
      name: 'Background',
      position: { x: 0, y: 0 },
      size: { width: canvas.width, height: canvas.height },
      fillColor: '#ffffff',
      image: { fit: 'contain' }
    };
  }
  if (kind === 'text') {
    return {
      ...base,
      kind,
      name: 'Text layer',
      position: { x: canvas.width * 0.1, y: canvas.height * 0.12 },
      size: { width: canvas.width * 0.8, height: 150 },
      text: 'New text',
      typography: {
        fontFamily: 'Arial',
        fontSize: 56,
        fontWeight: 'bold',
        fontStyle: 'normal',
        textAlign: 'left',
        color: '#000000',
        lineHeight: 1.1,
        letterSpacing: 0
      }
    };
  }
  if (kind === 'image') {
    return {
      ...base,
      kind,
      name: 'Image layer',
      position: { x: canvas.width * 0.1, y: canvas.height * 0.35 },
      size: { width: canvas.width * 0.8, height: canvas.height * 0.35 },
      image: {
        fit: 'contain',
        opacity: 1,
        borderColor: '#000000',
        borderWidth: 0
      },
      cornerRadius: 0
    };
  }
  return {
    ...base,
    kind,
    name: 'Shape layer',
    position: { x: canvas.width * 0.2, y: canvas.height * 0.25 },
    size:
      shapeType === 'straightLine'
        ? {
            width: canvas.width * 0.6,
            height: Math.max(8, canvas.height * 0.01)
          }
        : shapeType === 'wavyLine' || shapeType === 'arrow'
        ? { width: canvas.width * 0.6, height: canvas.height * 0.12 }
        : { width: canvas.width * 0.6, height: canvas.height * 0.2 },
    fillColor: '#3359c7',
    cornerRadius: 0,
    shapeType
  };
};
